import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Divider } from '@material-ui/core';
import { Link, useHistory, useParams } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';

import Loader from '../../common/loader';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { del, get, put } from '../../utils/api-services';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: 20,
        marginBottom: 24
    },
    updateBtn: {
        ...themeColors.yellowBtn
    },
    addBtn: {
        marginLeft: 20,
        padding: '.4rem .65rem',
        ...themeColors.greenBtn
    },
    removeBtn: {
        padding: '.4rem .65rem',
        ...themeColors.redBtn
    },
    tableContainer: {
        width: '100%',
        marginBottom: 24
    }
}));

const UserDetail = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const { id } = useParams();
    const classes = useStyles();
    const history = useHistory();
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedPolicies, setSelectedPolicies] = useState([]);
    const [state, setState] = useState({
        loading: true,
        user: {
            username: '',
            names: '',
            surnames: '',
            email: '',
            role: {}
        },
        groups: [],
        policies: [],
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'User group detail | IAM';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get(`/iamUser/${id}`);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                user: response.data,
                groups: response.groups,
                policies: response.policies
            }));
        } else {
            console.log('[UserDetail] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const validate = type => {
        if (type === 'group') {
            if (selectedGroups.length <= 0) {
                showToast('Select some group');
                return false;
            }

            return true;
        }

        if (type === 'policy') {
            if (selectedPolicies.length <= 0) {
                showToast('Select some policy');
                return false;
            }

            return true;
        }
    }

    const remove = async type => {
        const cond = validate(type);
        if (cond) {
            showLoader(true);
            const response = await del(`/iamUser/${id}/removeData`, { selectedGroups, selectedPolicies, type });
            if (response.status === 200) {
                if (type === 'group') setState(prev => ({ ...prev, groups: response.data }));
                if (type === 'policy') setState(prev => ({ ...prev, policies: response.data }));

                showToast(response.message);
            } else {
                console.log('[UserDetail] store error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const getPolicyType = ({ value }) => (value) ? 'CFG managed' : 'User managed';

    const groupColumns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
        { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    ];

    const policyColumns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
        { field: 'default', headerName: 'Type', flex: 1, minWidth: 200, valueGetter: getPolicyType },
        { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 }
    ];

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <h2>{'Detail'}</h2>

            <Grid container>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Username'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                type='text'
                                placeholder='Username'
                                value={state.user?.username ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Names'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                type='text'
                                placeholder='Names'
                                value={state.user?.names ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Surnames'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                type='text'
                                placeholder='Surnames'
                                value={state.user?.surnames ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Email'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                type='text'
                                placeholder='Email'
                                value={state.user?.email ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Role'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                type='text'
                                placeholder='Role'
                                value={state.user?.role?.name ?? 'N/A'}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Divider className={classes.divider} />

            <div className={classes.tableContainer}>
                <div className={classes.row}>
                    <h2>{'Groups'}</h2>

                    <div>
                        <Button
                            variant='contained'
                            className={classes.removeBtn}
                            disabled={!selectedGroups.length}
                            onClick={() => remove('group')}
                        >
                            {'Remove'}
                        </Button>
                        <Button
                            variant='contained'
                            className={classes.addBtn}
                        // onClick={() => remove('group')}
                        >
                            {'Add user to groups'}
                        </Button>
                    </div>
                </div>

                <DataGrid
                    rows={state.groups}
                    columns={groupColumns}
                    pageSize={10}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idiam_group}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={array => setSelectedGroups(array)}
                />
            </div>

            <Divider className={classes.divider} />

            <div className={classes.tableContainer}>
                <div className={classes.row}>
                    <h2>{'Policies attached'}</h2>

                    <div>
                        <Button
                            variant='contained'
                            className={classes.removeBtn}
                            disabled={!selectedPolicies.length}
                            onClick={() => remove('policy')}
                        >
                            {'Remove'}
                        </Button>
                        <Button
                            variant='contained'
                            className={classes.addBtn}
                            onClick={() => history.push(`/iam/users/detail/${id}/add-policies`)}
                        >
                            {'Add policies'}
                        </Button>
                    </div>
                </div>

                <DataGrid
                    rows={state.policies}
                    columns={policyColumns}
                    pageSize={20}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idiam_policy}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={array => setSelectedPolicies(array)}
                />
            </div>

        </div>
    );
}

export default UserDetail;